"use client";

import { useAuth } from "@clerk/clerk-react";
import Client from "@/api/client";
import { config } from "@/config/env";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18nInstance from "@/lib/i18n";

// Custom fetcher type compatible with Encore client
type ClientFetcher = (url: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/**
 * Hook providing an authenticated Encore client.
 * Automatically injects Clerk JWT token into all requests.
 */
export function useBackend() {
  const { getToken } = useAuth();
  const { i18n } = useTranslation();

  const client = useMemo(() => {
    const customFetcher: ClientFetcher = async (url, init) => {
      const token = await getToken();

      const headers: Record<string, string> = {
        ...(init?.headers as Record<string, string>),
        "Accept-Language": i18n.language,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      return fetch(url, {
        ...init,
        headers,
        credentials: "include",
      });
    };

    // Cast to satisfy Encore client's fetcher type
    return new Client(config.apiUrl, { fetcher: customFetcher as typeof fetch });
  }, [getToken, i18n.language]);

  return client;
}

/**
 * Creates an unauthenticated Encore client for public endpoints.
 * Use this for accessing shared videos without authentication.
 */
export function createPublicClient() {
  const headers = {
    "Accept-Language": i18nInstance.language,
  };
  
  return new Client(config.apiUrl, {
    requestInit: {
      headers,
    },
  });
}
